import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge } from '@/components/ui/Badge';
import {
  WorkspaceOrderedHeaders,
  WorkspaceOrderedMetricCells,
  WorkspaceOrderedTotalsRow,
} from '@/components/workspace/WorkspaceOrderedHeaders';
import { totalsFromMetricRows } from '@/utils/workspaceAdProductMetrics';
import {
  FilterSearch,
  FilterSelect,
  SelectionCheckbox,
  WorkspaceCheckboxCell,
  WorkspaceMiddleCell,
  ToolbarRow,
  WorkspaceSection,
  WorkspaceTablePagination,
  WorkspaceTableShell,
  tableRowClass,
  workspaceTableClass,
} from '@/components/workspace/WorkspaceSection';
import { WorkspaceTableSelectionActions } from '@/components/workspace/WorkspaceTableSelectionActions';
import type { WorkspaceDimensionSelectionProps } from '@/components/workspace/workspaceSelectionTypes';
import { useWorkspaceDemoActions } from '@/components/workspace/useWorkspaceDemoActions';
import {
  WorkspaceSearchTermNegateModal,
  WorkspaceSearchTermNegateProgressBanner,
  WorkspaceSearchTermNegateProgressModal,
} from '@/components/workspace/WorkspaceBulkActionModals';
import { ReportColumnPicker } from '@/components/ReportColumnPicker';
import { WorkspaceExportButton } from '@/components/workspace/WorkspaceExportButton';
import { WorkspaceMetricFiltersPopover } from '@/components/workspace/WorkspaceMetricFiltersPopover';
import { useMetricFilters } from '@/components/workspace/useMetricFilters';
import { useColumnOrder } from '@/components/workspace/useColumnOrder';
import { useWorkspaceFrozenLayout } from '@/components/workspace/workspaceStickyColumns';
import { useTableSort } from '@/components/workspace/useTableSort';
import {
  SEARCH_TERM_METRIC_COLUMNS,
  parseSearchTermTableRowKey,
  type SearchTermEnrichedRow,
} from '@/utils/workspaceSearchTermMetrics';
import {
  defaultColumnIds,
  workspaceColumnPickerOptions,
  type WorkspaceEntityColumnDef,
} from '@/components/workspace/workspaceColumnConfig';
import {
  keyLevelActionOptions,
  type DemoNegativeTarget,
} from '@/api/workspaceBulkActions';
import type { EntityCrossFilter, EntityGraph } from '@/utils/entityCrossFilter';
import { useServerPagination } from '@/hooks/useServerPagination';
import { useWorkspaceSearchTermsPage } from '@/hooks/useWorkspaceSearchTermsPage';
import { useWorkspaceSearchTermNegateJob } from '@/hooks/useWorkspaceSearchTermNegateJob';
import { useToast } from '@/components/ui/Toast';
import {
  exportWorkspaceTableCsv,
  fetchAllSearchTermRows,
  workspaceExportColumns,
} from '@/api/workspaceCsvExport';
import type { WorkspaceSearchTermNegateMatchType } from '@/api/workspaceSearchTermNegateTypes';

const SEARCH_TERM_ENTITY_COLUMNS: WorkspaceEntityColumnDef[] = [
  { id: 'searchTerm', label: 'Search Term' },
  { id: 'target', label: 'Target' },
  { id: 'campaign', label: 'Campaign' },
  { id: 'adGroup', label: 'Ad Group' },
  { id: 'matchType', label: 'Match Type' },
  { id: 'isNegated', label: 'Is Negated' },
  { id: 'tags', label: 'Tags' },
];

const SEARCH_TERM_REQUIRED_COLUMN_IDS = ['searchTerm', 'target'] as const;
const SEARCH_TERM_DEFAULT_FROZEN_IDS = ['searchTerm', 'target'] as const;
const SEARCH_TERM_COLUMN_IDS = defaultColumnIds(
  SEARCH_TERM_ENTITY_COLUMNS,
  SEARCH_TERM_METRIC_COLUMNS
);

function compareValues(left: string | number, right: string | number, dir: 'asc' | 'desc') {
  const result =
    typeof left === 'number' && typeof right === 'number'
      ? left - right
      : String(left).localeCompare(String(right), undefined, {
          numeric: true,
          sensitivity: 'base',
        });
  return dir === 'asc' ? result : -result;
}

function sortSearchTermRows(
  rows: SearchTermEnrichedRow[],
  sortBy: string,
  sortDir: 'asc' | 'desc'
) {
  return [...rows].sort((left, right) => {
    switch (sortBy) {
      case 'searchTerm':
        return compareValues(left.searchTerm, right.searchTerm, sortDir);
      case 'target':
        return compareValues(left.target, right.target, sortDir);
      case 'campaign':
        return compareValues(left.campaignName, right.campaignName, sortDir);
      case 'adGroup':
        return compareValues(left.adGroupName, right.adGroupName, sortDir);
      case 'matchType':
        return compareValues(left.matchType, right.matchType, sortDir);
      case 'isNegated':
        return compareValues(Number(left.isNegated), Number(right.isNegated), sortDir);
      case 'tags':
        return compareValues(left.tags, right.tags, sortDir);
      default:
        return compareValues(
          (left.metrics as Record<string, number>)[sortBy] ?? 0,
          (right.metrics as Record<string, number>)[sortBy] ?? 0,
          sortDir
        );
    }
  });
}

function resolveSearchTermNegativeTargets(
  pendingKeys: string[],
  rows: SearchTermEnrichedRow[],
  graph: EntityGraph | null
): DemoNegativeTarget[] {
  if (pendingKeys.length === 0) return [];
  const byKey = new Map(rows.map((row) => [row.rowKey, row]));
  const targets: DemoNegativeTarget[] = [];
  const seen = new Set<string>();

  for (const key of pendingKeys) {
    const row = byKey.get(key);
    const parsed = row ? null : parseSearchTermTableRowKey(key);
    const campaignId = row?.campaignId ?? parsed?.campaignId ?? '';
    const adGroupId = row?.adGroupId ?? parsed?.adGroupId ?? '';
    const text = (row?.searchTerm ?? parsed?.searchTerm ?? '').trim();
    if (!campaignId || !text) continue;
    const negateKey = `${campaignId}|${adGroupId}|${text.toLowerCase()}`;
    if (seen.has(negateKey)) continue;
    seen.add(negateKey);
    if (row) {
      targets.push({
        text: row.searchTerm,
        campaignId: row.campaignId,
        campaignName: row.campaignName,
        adGroupId: row.adGroupId,
        adGroupName: row.adGroupName,
      });
      continue;
    }

    const campaign = graph?.campaigns.find((c) => c.campaignId === campaignId);
    const adGroup = adGroupId ? graph?.indices.adGroupsById.get(adGroupId) : undefined;
    targets.push({
      text,
      campaignId,
      campaignName: campaign?.name ?? campaignId,
      adGroupId: adGroupId ?? '',
      adGroupName: adGroup?.name ?? adGroupId ?? '—',
    });
  }

  return targets;
}

export function WorkspaceSearchTermsTable({
  profileId,
  startDate,
  endDate,
  previousStartDate,
  previousEndDate,
  compareEnabled,
  graph,
  appliedFilter,
  enabled,
  rangeSubtitle,
  isFiltered,
  selection,
  isDemo = false,
  demoCampaigns = [],
  demoAdGroups = [],
}: {
  profileId: string;
  startDate: string;
  endDate: string;
  previousStartDate: string;
  previousEndDate: string;
  compareEnabled: boolean;
  graph: EntityGraph | null;
  appliedFilter: EntityCrossFilter;
  enabled: boolean;
  rangeSubtitle: string;
  isFiltered: boolean;
  selection: WorkspaceDimensionSelectionProps;
  isDemo?: boolean;
  demoCampaigns?: Array<{ id: string; name: string }>;
  demoAdGroups?: Array<{ id: string; name: string; campaignId: string }>;
}) {
  const { showToast } = useToast();
  const { openDemoAction, demoModals } = useWorkspaceDemoActions({
    showToast,
    campaigns: demoCampaigns,
    adGroups: demoAdGroups,
  });
  const negateJob = useWorkspaceSearchTermNegateJob(profileId);
  const [search, setSearch] = useState('');
  const [matchFilter, setMatchFilter] = useState('ALL');
  const [negatedFilter, setNegatedFilter] = useState('ALL');
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [negateModalOpen, setNegateModalOpen] = useState(false);
  const [progressModalOpen, setProgressModalOpen] = useState(false);
  const [launchingNegate, setLaunchingNegate] = useState(false);
  const tableSort = useTableSort({ defaultSortBy: 'cost', defaultSortDir: 'desc' });
  const metricFilters = useMetricFilters({
    tableId: 'search-terms',
    metrics: SEARCH_TERM_METRIC_COLUMNS,
  });
  const columnOrder = useColumnOrder(
    SEARCH_TERM_COLUMN_IDS,
    undefined,
    undefined,
    SEARCH_TERM_DEFAULT_FROZEN_IDS
  );
  const freezeLayout = useWorkspaceFrozenLayout(
    columnOrder.frozenColumnIds,
    columnOrder.displayOrder.join('\0')
  );

  const resetKey = [
    search,
    matchFilter,
    negatedFilter,
    startDate,
    endDate,
    JSON.stringify(appliedFilter),
    metricFilters.serialized,
  ].join('|');

  const { page, pageSize, setPage, setPageSize } = useServerPagination(resetKey);

  useEffect(() => {
    setSelectAllMatching(false);
  }, [resetKey]);

  const pageResult = useWorkspaceSearchTermsPage({
    profileId,
    startDate,
    endDate,
    previousStartDate,
    previousEndDate,
    compareEnabled,
    graph,
    appliedFilter,
    page,
    pageSize,
    filters: { search, matchType: matchFilter, negated: negatedFilter },
    metricFilters: metricFilters.payload,
    enabled,
  });

  const visible = useMemo(() => {
    return sortSearchTermRows(pageResult.rows, tableSort.sortBy, tableSort.sortDir);
  }, [pageResult.rows, tableSort.sortBy, tableSort.sortDir]);

  const visibleIds = visible.map((r) => r.rowKey);
  const allSelected =
    selectAllMatching ||
    (visibleIds.length > 0 && visibleIds.every((id) => selection.pendingIds.includes(id)));
  const actionSelectionCount = selectAllMatching
    ? pageResult.totalCount
    : selection.selectionCount;
  const subtitleParts = [`${pageResult.totalCount} total`, rangeSubtitle];
  if (isFiltered) subtitleParts.push('Filtered to selected entities');
  const isTableLoading = pageResult.isPending;

  const { currentTotals, previousTotals } = useMemo(() => {
    const currentRows = visible.map((row) => row.metrics);
    const previousRows = visible.map((row) => {
      const prev = pageResult.previousByKey.get(row.rowKey);
      return prev?.metrics ?? {
        impressions: 0,
        clicks: 0,
        cost: 0,
        purchases30d: 0,
        sales30d: 0,
      };
    });
    return {
      currentTotals: totalsFromMetricRows(currentRows),
      previousTotals: totalsFromMetricRows(previousRows),
    };
  }, [visible, pageResult.previousByKey]);

  const negativeTargets = useMemo(
    () => resolveSearchTermNegativeTargets(selection.pendingIds, pageResult.rows, graph),
    [selection.pendingIds, pageResult.rows, graph]
  );

  const jobActive = launchingNegate || negateJob.isRunning || negateJob.job != null;
  const showProgressModal = progressModalOpen;
  const showProgressBanner = jobActive && !progressModalOpen;
  const progressRunning = launchingNegate || negateJob.isRunning;

  const retryFailedNegatives = useCallback(() => {
    if (!negateJob.job) return;
    setProgressModalOpen(true);
    void negateJob.retryFailed(negateJob.job.id).then((final) => {
      const applied = final.totals.applied ?? 0;
      const failed = final.totals.failed ?? 0;
      if (failed > 0) {
        showToast(`Retry finished: ${applied} applied, ${failed} still failed`, 'error');
      } else {
        showToast(
          `Retry finished: ${applied} negative${applied === 1 ? '' : 's'} added`,
          'success'
        );
      }
    });
  }, [negateJob, showToast]);

  const handleActionSelect = useCallback(
    (actionId: string) => {
      if (actionId !== 'add_negative') {
        if (!isDemo) return;
        openDemoAction(actionId, {
          entityLabel: 'search term',
          selectionCount: actionSelectionCount,
          negativeTargets,
        });
        return;
      }
      if (actionSelectionCount <= 0) {
        showToast('Select at least one search term', 'error');
        return;
      }
      if (isDemo) {
        openDemoAction(actionId, {
          entityLabel: 'search term',
          selectionCount: actionSelectionCount,
          negativeTargets,
        });
        return;
      }
      setNegateModalOpen(true);
    },
    [
      isDemo,
      openDemoAction,
      actionSelectionCount,
      negativeTargets,
      showToast,
    ]
  );

  const handleNegateConfirm = useCallback(
    async (matchType: WorkspaceSearchTermNegateMatchType) => {
      setNegateModalOpen(false);
      setLaunchingNegate(true);
      setProgressModalOpen(true);
      try {
        const body = selectAllMatching
          ? {
              matchType,
              selectAllMatching: true as const,
              startDate,
              endDate,
              appliedFilter: appliedFilter as unknown as Record<string, unknown>,
              columnFilters: {
                search: search.trim() || undefined,
                matchType: matchFilter !== 'ALL' ? matchFilter : undefined,
                negated: negatedFilter !== 'ALL' ? negatedFilter : undefined,
              },
            }
          : {
              matchType,
              searchTermKeys: [...selection.pendingIds],
            };
        const final = await negateJob.start(body);
        const applied = final.totals.applied ?? 0;
        const failed = final.totals.failed ?? 0;
        const skipped = final.totals.skipped ?? 0;
        if (final.status === 'failed') {
          showToast(final.applyProgress.error || 'Failed to add negative keywords', 'error');
        } else if (failed > 0) {
          showToast(
            `Added ${applied} negative${applied === 1 ? '' : 's'}, ${failed} failed`,
            'error'
          );
        } else {
          showToast(
            `Added ${applied} negative keyword${applied === 1 ? '' : 's'}${
              skipped > 0 ? ` (${skipped} skipped)` : ''
            }`,
            'success'
          );
          setSelectAllMatching(false);
          selection.onClearSelection();
        }
      } catch (error) {
        const detail =
          error &&
          typeof error === 'object' &&
          'response' in error &&
          (error as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
        showToast(
          typeof detail === 'string'
            ? detail
            : error instanceof Error
              ? error.message
              : 'Failed to add negative keywords',
          'error'
        );
        setProgressModalOpen(false);
      } finally {
        setLaunchingNegate(false);
      }
    },
    [
      selectAllMatching,
      appliedFilter,
      startDate,
      endDate,
      search,
      matchFilter,
      negatedFilter,
      selection,
      negateJob,
      showToast,
    ]
  );

  return (
    <WorkspaceSection
      title="Search Terms"
      countLabel={subtitleParts.join(' · ')}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <WorkspaceMetricFiltersPopover metrics={SEARCH_TERM_METRIC_COLUMNS} {...metricFilters} />
          <WorkspaceExportButton
            onExport={async () => {
              try {
                const fetched = await fetchAllSearchTermRows({
                  profileId,
                  startDate,
                  endDate,
                  appliedFilter: appliedFilter as unknown as Record<string, unknown>,
                  search: search.trim() || undefined,
                  matchType: matchFilter !== 'ALL' ? matchFilter : undefined,
                  metricFilters: metricFilters.payload,
                });
                const columns = workspaceExportColumns(
                  columnOrder.visibleIds,
                  SEARCH_TERM_ENTITY_COLUMNS,
                  SEARCH_TERM_METRIC_COLUMNS
                );
                const rows = fetched.rows.map((row) => {
                  const metrics =
                    row.metrics && typeof row.metrics === 'object'
                      ? (row.metrics as Record<string, unknown>)
                      : row;
                  return {
                    searchTerm: row.searchTerm ?? row.query ?? '',
                    target: row.target ?? row.keyword ?? '',
                    campaign: row.campaignName ?? row.campaign ?? '',
                    adGroup: row.adGroupName ?? row.adGroup ?? '',
                    matchType: row.matchType ?? '',
                    isNegated: row.isNegated ?? '',
                    tags: row.tags ?? '',
                    metrics,
                  };
                });
                exportWorkspaceTableCsv({
                  filenamePrefix: 'workspace_search_terms',
                  startDate,
                  endDate,
                  columns,
                  rows,
                });
                if (fetched.truncated) {
                  showToast(
                    `Exported first ${fetched.rows.length.toLocaleString()} of ${fetched.totalCount.toLocaleString()} rows`,
                    'error'
                  );
                } else {
                  showToast(`Exported ${fetched.rows.length.toLocaleString()} rows`, 'success');
                }
              } catch (error) {
                showToast(
                  error instanceof Error ? error.message : 'Failed to export search terms',
                  'error'
                );
              }
            }}
          />
          <ReportColumnPicker
            columns={workspaceColumnPickerOptions(SEARCH_TERM_ENTITY_COLUMNS, SEARCH_TERM_METRIC_COLUMNS)}
            selected={columnOrder.visibleIds}
            onChange={columnOrder.setVisibleIds}
            requiredColumnIds={SEARCH_TERM_REQUIRED_COLUMN_IDS}
            panelTitle="Visible columns"
          />
        </div>
      }
    >
      <ToolbarRow>
        <div className="flex flex-wrap items-end gap-3">
          <FilterSearch
            id="workspace-search-term-search"
            label="Search terms"
            value={search}
            onChange={setSearch}
          />
          <FilterSelect
            id="workspace-search-term-match"
            label="Match type"
            value={matchFilter}
            onChange={setMatchFilter}
          >
            <option value="ALL">All</option>
            <option value="EXACT">Exact</option>
            <option value="PHRASE">Phrase</option>
            <option value="BROAD">Broad</option>
          </FilterSelect>
          <FilterSelect
            id="workspace-search-term-negated"
            label="Is negated"
            value={negatedFilter}
            onChange={setNegatedFilter}
          >
            <option value="ALL">All</option>
            <option value="YES">Negated</option>
            <option value="NO">Not negated</option>
          </FilterSelect>
          <WorkspaceTableSelectionActions
            entityLabel="search term"
            selectionCount={actionSelectionCount}
            hasPendingChanges={selection.hasPendingChanges}
            canApply={selection.canApply && !selectAllMatching}
            onApply={selection.onApply}
            onClearSelection={() => {
              setSelectAllMatching(false);
              selection.onClearSelection();
            }}
            disabled={isTableLoading || negateJob.isRunning}
            actions={keyLevelActionOptions(isDemo)}
            onActionSelect={handleActionSelect}
          />
        </div>
      </ToolbarRow>
      {allSelected &&
      !selectAllMatching &&
      pageResult.totalCount > visible.length ? (
        <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-surface-muted px-3 py-2 text-sm text-foreground">
          <span>
            All {visible.length} search terms on this page are selected.
          </span>
          <button
            type="button"
            className="font-medium text-foreground underline underline-offset-2"
            onClick={() => setSelectAllMatching(true)}
          >
            Select all {pageResult.totalCount} matching search terms
          </button>
        </div>
      ) : null}
      {selectAllMatching ? (
        <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-surface-muted px-3 py-2 text-sm text-foreground">
          <span>All {pageResult.totalCount} matching search terms are selected.</span>
          <button
            type="button"
            className="font-medium text-foreground underline underline-offset-2"
            onClick={() => {
              setSelectAllMatching(false);
              selection.onClearSelection();
            }}
          >
            Clear selection
          </button>
        </div>
      ) : null}
      {showProgressBanner ? (
        <WorkspaceSearchTermNegateProgressBanner
          job={negateJob.job}
          isRunning={progressRunning}
          onOpen={() => setProgressModalOpen(true)}
          onRetryFailed={retryFailedNegatives}
          onDismiss={() => {
            if ((negateJob.job?.totals.failed ?? 0) > 0) negateJob.reset();
          }}
        />
      ) : null}
      <WorkspaceTableShell
        isEmpty={!pageResult.isPending && visible.length === 0}
        isLoading={isTableLoading}
        emptyMessage={
          isFiltered
            ? 'No search terms match the current selection.'
            : 'No search term report rows for this range.'
        }
        footer={
          <WorkspaceTablePagination
            page={page}
            pageSize={pageSize}
            total={pageResult.totalCount}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            disabled={isTableLoading}
          />
        }
      >
        <table ref={freezeLayout.tableRef} className={workspaceTableClass.table}>
          <thead>
            <tr>
              <WorkspaceCheckboxCell isHeader>
                <SelectionCheckbox
                  checked={allSelected}
                  label="Select all visible search terms"
                  disabled={isTableLoading}
                  onChange={(checked) => {
                    setSelectAllMatching(false);
                    selection.onToggleAll(visibleIds, checked);
                  }}
                />
              </WorkspaceCheckboxCell>
              <WorkspaceOrderedHeaders
                order={columnOrder.displayOrder}
                entityColumns={SEARCH_TERM_ENTITY_COLUMNS}
                metricColumns={SEARCH_TERM_METRIC_COLUMNS}
                sortBy={tableSort.sortBy}
                sortDir={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                onFreeze={columnOrder.freezeColumn}
                frozenColumnIds={columnOrder.frozenColumnIds}
                pinnedFrozenColumnId={columnOrder.pinnedFrozenColumnId}
                frozenLeftById={freezeLayout.frozenLeftById}
                onDragStartColumn={columnOrder.startDrag}
                onDragHoverColumn={columnOrder.hoverReorder}
                onDropColumn={columnOrder.commitDrop}
                onDragEndColumn={columnOrder.endDrag}
                activeDragColumnId={columnOrder.activeDragColumnId}
                hoverColumnId={columnOrder.hoverColumnId}
                hoverPosition={columnOrder.hoverPosition}
                hoverInsertIndex={columnOrder.hoverInsertIndex}
              />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {visible.map((row) => {
              const prev = pageResult.previousByKey.get(row.rowKey);
              const selected =
                selectAllMatching || selection.pendingIds.includes(row.rowKey);
              return (
                <tr key={row.rowKey} className={tableRowClass(selected)}>
                  <WorkspaceCheckboxCell>
                    <SelectionCheckbox
                      checked={selected}
                      label={`Select search term ${row.searchTerm}`}
                      disabled={isTableLoading}
                      onChange={(checked) => {
                        setSelectAllMatching(false);
                        selection.onToggleId(row.rowKey, checked);
                      }}
                    />
                  </WorkspaceCheckboxCell>
                  <WorkspaceOrderedMetricCells
                    order={columnOrder.displayOrder}
                    frozenColumnIds={columnOrder.frozenColumnIds}
                    frozenLeftById={freezeLayout.frozenLeftById}
                    metrics={row.metrics}
                    previousMetrics={prev?.metrics ?? null}
                    compareEnabled={compareEnabled}
                    isLoading={isTableLoading}
                    metricColumns={SEARCH_TERM_METRIC_COLUMNS}
                    activeDragColumnId={columnOrder.activeDragColumnId}
                    hoverColumnId={columnOrder.hoverColumnId}
                    hoverPosition={columnOrder.hoverPosition}
                    renderEntityCell={(columnId, meta) => {
                      if (columnId === 'searchTerm') {
                        return (
                          <td style={meta.style} className={`${workspaceTableClass.td} ${meta.className} font-medium text-foreground`}>
                            {row.searchTerm}
                          </td>
                        );
                      }
                      if (columnId === 'target') {
                        return <td style={meta.style} className={`${workspaceTableClass.td} ${meta.className} text-foreground-muted`}>{row.target}</td>;
                      }
                      if (columnId === 'campaign') {
                        return (
                          <td style={meta.style} className={`${workspaceTableClass.td} ${meta.className} text-foreground-muted`}>
                            {row.campaignName}
                          </td>
                        );
                      }
                      if (columnId === 'adGroup') {
                        return (
                          <td style={meta.style} className={`${workspaceTableClass.td} ${meta.className} text-foreground-muted`}>
                            {row.adGroupName}
                          </td>
                        );
                      }
                      if (columnId === 'matchType') {
                        return (
                          <WorkspaceMiddleCell style={meta.style} className={meta.className} alignToTitle>
                            <span className="text-xs font-medium uppercase tracking-wider">
                              {row.matchType}
                            </span>
                          </WorkspaceMiddleCell>
                        );
                      }
                      if (columnId === 'isNegated') {
                        return (
                          <WorkspaceMiddleCell style={meta.style} className={meta.className} alignToTitle>
                            {row.isNegated ? <Badge variant="warning">Yes</Badge> : 'No'}
                          </WorkspaceMiddleCell>
                        );
                      }
                      if (columnId === 'tags') {
                        return (
                          <td style={meta.style} className={`${workspaceTableClass.td} ${meta.className} text-foreground-muted`}>
                            {row.tags || '—'}
                          </td>
                        );
                      }
                      return null;
                    }}
                  />
                </tr>
              );
            })}
          </tbody>
          {visible.length > 0 ? (
            <tfoot>
              <WorkspaceOrderedTotalsRow
                order={columnOrder.displayOrder}
                frozenColumnIds={columnOrder.frozenColumnIds}
                frozenLeftById={freezeLayout.frozenLeftById}
                current={currentTotals}
                previous={previousTotals}
                compareEnabled={compareEnabled}
                isLoading={isTableLoading}
                metricColumns={SEARCH_TERM_METRIC_COLUMNS}
              />
            </tfoot>
          ) : null}
        </table>
      </WorkspaceTableShell>
      {demoModals}
      {negateModalOpen ? (
        <WorkspaceSearchTermNegateModal
          targets={selectAllMatching ? [] : negativeTargets}
          selectionCount={actionSelectionCount}
          pending={negateJob.isRunning}
          onCancel={() => setNegateModalOpen(false)}
          onConfirm={(matchType) => {
            void handleNegateConfirm(matchType);
          }}
        />
      ) : null}
      {showProgressModal ? (
        <WorkspaceSearchTermNegateProgressModal
          job={negateJob.job}
          failedResults={negateJob.failedResults}
          isRunning={progressRunning}
          onClose={() => {
            setProgressModalOpen(false);
            const failed = negateJob.job?.totals.failed ?? 0;
            if (!progressRunning && failed === 0) negateJob.reset();
          }}
          onRunInBackground={() => setProgressModalOpen(false)}
          onRetryFailed={retryFailedNegatives}
        />
      ) : null}
    </WorkspaceSection>
  );
}
